import { MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Skeleton from '@mui/material/Skeleton'
import { ROUTES } from '@common/constants/routes'
import { AtlasObjectType, toAtlasRouterPath } from '@common/helpers/atlasUrls'
import {
  atlasObjectRefKey,
  isResolvedDetail,
  isResolvedPreview,
} from '@common/interfaces/atlasObjects'
import { ATLAS_OBJECT_TYPE_LABELS } from '../../constants/atlasObjectLabels'
import { isMessagesRoute, shouldOpenMessageInBubble } from '../../helpers/messageNotifications'
import { usePermissions } from '../../helpers/permissionContext'
import {
  cancelHidePreview,
  clearPreviewAnchor,
  getPreviewAnchor,
  scheduleHidePreview,
  usePreviewAnchor,
} from '../../hooks/atlasPreviewAnchor'
import { useAtlasModulePanel } from '../../hooks/useAtlasModulePanel'
import { useAtlasObjectPreviews } from '../../hooks/useAtlasObjectPreviews'
import { isModifiedClick } from '../../hooks/useAtlasPreviewTrigger'
import { useGlobalAtlasLinkPreview } from '../../hooks/useGlobalAtlasLinkPreview'
import { useIsMobile } from '../../hooks/useIsMobile'
import Button from '../Common/Button'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import { PHOTO_SUBJECTS } from '../Common/ObjectLinkChip/ObjectLinkChip'
import ProfilePhoto from '../Common/ProfilePhoto'
import Text from '../Common/Text'
import styles from './AtlasPreviewCard.module.scss'
import AtlasPreviewFields from './AtlasPreviewFields'
import { PropsFromRedux } from './index'

const SKELETON_ROWS = 4
const NO_REFS: never[] = []
const POPPER_MODIFIERS = [
  { name: 'offset', options: { offset: [0, 8] } },
  { name: 'preventOverflow', options: { padding: 8 } },
]

export type IProps = PropsFromRedux

const AtlasPreviewCard = ({ previewState, closeAtlasPreview, requestStartMessagesDm }: IProps) => {
  const { target, detail, isLoading, hasError } = previewState
  useGlobalAtlasLinkPreview()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const taskId = new URLSearchParams(search).get('taskId')
  const anchor = usePreviewAnchor()
  const { isInternalUser } = usePermissions()
  const isMobile = useIsMobile()
  const { canOpenInPanel, openInPanel } = useAtlasModulePanel()

  const refs = useMemo(() => (target ? [target.ref] : NO_REFS), [target])
  const previewsByKey = useAtlasObjectPreviews(refs)
  const cachedPreview = target ? previewsByKey[atlasObjectRefKey(target.ref)] : undefined
  const resolvedPreview = cachedPreview && isResolvedPreview(cachedPreview) ? cachedPreview : null

  const closePreview = useCallback(() => {
    clearPreviewAnchor()
    closeAtlasPreview()
  }, [closeAtlasPreview])

  // The record lives in redux, the chip it hangs from in the anchor store: whichever
  // side lets go first takes the other with it.
  useEffect(() => {
    if (target && !anchor.element) closeAtlasPreview()
    if (!target && anchor.element) clearPreviewAnchor()
  }, [anchor.element, target, closeAtlasPreview])

  // A chip that leaves the page (its dialog, panel or bubble closed) never fires mouseleave.
  useEffect(() => {
    const { element } = anchor
    if (!element) return
    const closeIfDetached = () => {
      if (!element.isConnected) closePreview()
    }
    const observer = new MutationObserver(closeIfDetached)
    observer.observe(document.body, { childList: true, subtree: true })
    closeIfDetached()
    return () => observer.disconnect()
  }, [anchor, closePreview])

  // Not every search change: pages keep their filters in the query string, and editing one
  // must not close the preview. A new `taskId` opens the task panel, which this would cover.
  const locationKey = `${pathname}|${taskId ?? ''}`
  const locationOnOpen = useRef(locationKey)
  useEffect(() => {
    if (target && locationKey !== locationOnOpen.current) {
      closePreview()
    }
    locationOnOpen.current = locationKey
  }, [locationKey, target, closePreview])

  useEffect(() => {
    if (!target) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      if (document.querySelector('.MuiModal-root:not(.MuiPopover-root)')) return
      closePreview()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [target, closePreview])

  const handleClickAway = useCallback(
    (event: globalThis.MouseEvent | TouchEvent) => {
      // A click on the chip itself is the chip's to handle: it pins or unpins the card.
      const { element } = getPreviewAnchor()
      if (element && event.target instanceof Node && element.contains(event.target)) return
      closePreview()
    },
    [closePreview],
  )

  const resolvedDetail = detail && isResolvedDetail(detail) ? detail : null
  // The detail may describe another identity of the linked record, at its own page.
  const recordUrl = resolvedDetail?.url ?? target?.url
  const recordPath = recordUrl ? toAtlasRouterPath(recordUrl) : ''

  const handleNameClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (isModifiedClick(event)) return
      event.preventDefault()
      closePreview()
      void navigate(recordPath)
    },
    [closePreview, navigate, recordPath],
  )

  // Over another page the bubble takes the draft; elsewhere AIM itself has to be opened.
  const handleMessage = useCallback(() => {
    if (!target) return
    closePreview()
    requestStartMessagesDm([target.ref.id])
    if (
      !isMessagesRoute(pathname) &&
      !shouldOpenMessageInBubble(pathname, isInternalUser, isMobile)
    ) {
      void navigate(ROUTES.MESSAGES)
    }
  }, [target, closePreview, requestStartMessagesDm, pathname, isInternalUser, isMobile, navigate])

  const handleOpenInPanel = useCallback(() => {
    if (!target) return
    closePreview()
    // The record the detail resolved to, which a link can name by another identity.
    const { type, id } = resolvedDetail ?? target.ref
    openInPanel({ ref: { type, id }, url: recordPath })
  }, [target, closePreview, openInPanel, resolvedDetail, recordPath])

  if (!target || !anchor.element) return null

  const isRestricted = (!!detail && !resolvedDetail) || cachedPreview?.restricted === true

  const renderBody = () => {
    if (isRestricted) {
      return (
        <Text variant="bodyLg" tone="secondary" className={styles.message}>
          You don&apos;t have access to this record.
        </Text>
      )
    }
    if (hasError) {
      return (
        <Text variant="bodyLg" tone="secondary" className={styles.message}>
          Couldn&apos;t load this record. It may have been deleted.
        </Text>
      )
    }
    if (!resolvedDetail) {
      return Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <Skeleton key={index} height={24} />
      ))
    }
    return <AtlasPreviewFields fields={resolvedDetail.fields} />
  }

  const name = resolvedDetail?.name || resolvedPreview?.name || ''
  const imageUrl = resolvedDetail?.imageUrl ?? resolvedPreview?.imageUrl ?? null
  const { type, id } = resolvedDetail ?? target.ref
  const photoSubject = type !== AtlasObjectType.Task ? PHOTO_SUBJECTS[type] : undefined
  const isOpenableInPanel = canOpenInPanel && !!recordPath && !isRestricted && !hasError
  const canMessage = !!resolvedDetail?.canMessage

  return (
    <Popper
      open
      anchorEl={anchor.element}
      placement="bottom-start"
      modifiers={POPPER_MODIFIERS}
      className={styles.popper}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <Paper
          className={styles.card}
          role="dialog"
          aria-label={name || ATLAS_OBJECT_TYPE_LABELS[type]}
          onMouseEnter={cancelHidePreview}
          onMouseLeave={scheduleHidePreview}
          data-cy="atlas-preview-card"
        >
          <header className={styles.header}>
            {type !== AtlasObjectType.Task && (
              <ProfilePhoto
                name={name}
                subjectType={photoSubject}
                id={photoSubject ? id : undefined}
                iconUrl={imageUrl}
                isLogo={type !== AtlasObjectType.Person && type !== AtlasObjectType.User}
                size="medium"
              />
            )}
            <div className={styles.titleBlock}>
              <Text variant="eyebrow" tone="tertiary" casing="none">
                {ATLAS_OBJECT_TYPE_LABELS[type]}
              </Text>
              <Text
                variant="cardTitle"
                tone="primary"
                casing="none"
                className={styles.title}
                title={name}
              >
                {name && !isRestricted && recordPath ? (
                  <a
                    href={recordPath}
                    className={styles.titleLink}
                    onClick={handleNameClick}
                    data-cy="atlas-preview-name-link"
                  >
                    {name}
                  </a>
                ) : (
                  name || (isLoading ? <Skeleton width={160} /> : ATLAS_OBJECT_TYPE_LABELS[type])
                )}
              </Text>
            </div>
            <IconButton title="Close preview" size="small" onClick={closePreview}>
              <Icon name="x" size="sm" />
            </IconButton>
          </header>

          <div className={styles.body}>{renderBody()}</div>

          {(isOpenableInPanel || canMessage) && (
            <footer className={styles.footer}>
              {isOpenableInPanel && (
                <Button
                  size="small"
                  startIcon={<Icon name="panel-open" size="sm" />}
                  onClick={handleOpenInPanel}
                  data-cy="atlas-preview-open-in-panel"
                >
                  Open in panel
                </Button>
              )}
              {canMessage && (
                <Button size="small" onClick={handleMessage} data-cy="atlas-preview-message">
                  Message
                </Button>
              )}
            </footer>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}

export default AtlasPreviewCard
